// PLAT-10270 (F1) — decide whether the translated-captions view gets
// break-editing handlers at its Transcript mount. Extracted as a pure,
// testable unit so the criterion-5 disjointness fix has a durable regression
// guard (a focused test of the mount's gating expression).

import { captionMergeWouldCrossSpeakers } from '@src/editor/commands/karaoke/MergeAdjacentCaptionClipsCommand';

import type { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import type { ToastState } from '@src/stores/ITranscriptVideoCreator';

/**
 * The synthetic caption break/merge actions the Transcript builds from the
 * editor. Structural shape of `syntheticBreakActions` in Transcript.tsx
 * (`{ add, remove, merge } | null`).
 */
export interface SyntheticBreakActions {
  add: (
    lineId: string,
    phraseOriginalIndices: number[],
    textPosition: number,
  ) => void;
  remove: (breakId: string) => void;
  merge: (rightLineId: string) => void;
}

/**
 * The break-editing handler bundle `TranslatedCaptionsView` consumes
 * (`syntheticBreakHandlers` prop). Matches its internal `SyntheticBreakHandlers`
 * shape structurally.
 */
export interface CaptionBreakHandlers {
  onAddBreak: (
    lineId: string,
    phraseOriginalIndices: number[],
    textPosition: number,
  ) => void;
  onRemoveBreak: (breakId: string) => void;
  onMerge: (rightLineId: string) => void;
}

/**
 * Resolve the break-editing handlers for the `TranslatedCaptionsView` mount.
 *
 * Returns `undefined` — which DISABLES break-editing, because
 * `breakEditingEnabled = !isPlaying && !!syntheticBreakHandlers` in
 * TranslatedCaptionsView, so with undefined handlers its window capture-phase
 * Backspace listener never registers — whenever either:
 *   - there are no editor actions (`syntheticBreakActions` is null), or
 *   - the Translation Review workspace is open (`translationReviewOpen`).
 *
 * Disabling captions break-editing while review is open is the PLAT-10270 SPEC
 * acceptance-criterion-5 fix (F1): the review recombine Backspace
 * (`useFragmentRecombine`) and the captions merge-adjacent-clips Backspace are
 * BOTH window capture-phase keydown listeners that unconditionally consume
 * Backspace. In the clip-preview host the captions Transcript registers before
 * the review workspace, so with both live it eats a Backspace meant for
 * recombine. Making them mutually exclusive by construction (only one listener
 * is ever live) keeps the recombine gesture from affecting the captions editor.
 */
export function resolveCaptionBreakHandlers(
  syntheticBreakActions: SyntheticBreakActions | null,
  translationReviewOpen: boolean,
): CaptionBreakHandlers | undefined {
  if (!syntheticBreakActions || translationReviewOpen) return undefined;
  return {
    onAddBreak: syntheticBreakActions.add,
    onRemoveBreak: syntheticBreakActions.remove,
    onMerge: syntheticBreakActions.merge,
  };
}

/**
 * PLAT-10945 — what {@link mergeCaptionRows} needs from the editor. Narrow on
 * purpose: the join itself, and read-only access to the caption rows so a
 * refusal can be explained.
 */
export interface CaptionMergeEditor {
  commandMergeAdjacentCaptionClips(rightClipId: string): boolean;
  getKaraokeTrack(): { karaokeClips: KaraokeClip[] } | undefined;
}

/**
 * PLAT-10945 — shown when a user tries to join two caption rows that belong to
 * different speakers. Sentence case: the design system reserves Title Case for
 * control labels, and this is prose.
 */
export const CAPTION_MERGE_CROSS_SPEAKER_MESSAGE =
  'These caption rows belong to different speakers. Joining them would put two people’s words in one row.';

/**
 * PLAT-10945 — join the caption row `rightLineId` into the row above it, and
 * tell the user when that is refused because the two rows belong to different
 * speakers.
 *
 * Backspace at the start of a caption row, and Delete at the end of the row
 * above it, both land here. The command's boolean used to be discarded, so a
 * refused key press looked exactly like a broken keyboard — nothing moved and
 * nothing said why. A user who presses a key and gets silence presses it
 * again, then concludes the product is broken.
 *
 * Only the speaker refusal is reported. The command's other two refusals (no
 * caption track, no row above) are states the user cannot act on and were
 * silent before; they stay silent, because naming a speaker conflict that did
 * not happen would be worse than saying nothing.
 *
 * Returns whether the rows were joined.
 */
export function mergeCaptionRows(
  editor: CaptionMergeEditor,
  rightLineId: string,
  showToast: (toast: NonNullable<ToastState>) => void,
): boolean {
  if (editor.commandMergeAdjacentCaptionClips(rightLineId)) return true;
  // A refusal mutates nothing, so the rows can be re-read to work out which
  // refusal it was. `captionMergeWouldCrossSpeakers` is the command's own
  // rule, imported rather than restated.
  const clips = editor.getKaraokeTrack()?.karaokeClips ?? [];
  if (captionMergeWouldCrossSpeakers(clips, rightLineId)) {
    showToast({
      // 'warning' and 'error' render identically (red, exclamation icon);
      // 'warning' is the honest one — nothing failed, the join was declined.
      state: 'warning',
      message: CAPTION_MERGE_CROSS_SPEAKER_MESSAGE,
      position: 'top',
      delay: 4000,
    });
  }
  return false;
}
